/**
 * Relay sync between M3's Reach and the strategic core (plan I2; spec §3.4 S-M3-6: the worker is the source of
 * truth for which relays are online, the routing tables read it).
 *
 * - **Live:** a reboot that ends online (`REV.rebootEnd` with `b === REBOOT_END.online`, `a` = the relay's index in
 *   `galaxy.relays`) becomes `setRelayOnline` on the client. The scan runs every Reach step with its own cursor and
 *   allocates nothing unless a reboot ended (a rare event: one command object then).
 * - **Load:** with a saved Reach the worker's relay states are copied into `ReachState.relays` (relays mid-reboot
 *   keep theirs). With a fresh Reach (no strategic save for this career yet), M3's online relays are sent to the
 *   worker instead, so relays the player already relinked stay online (D-TBD: fresh Reach adopts M3's relays).
 */
import { eventSlot, firstReadable, type EventRing } from '../combat/events'
import { logger } from '../core/logger'
import type { Galaxy } from '../galaxy/types'
import { REV } from '../reach/events'
import { ensureRelayState } from '../reach/state/relays'
import type { ReachState } from '../reach/types'
import { REBOOT_END } from '../relink/reboot'
import type { CommandResult, GalaxyEconomyView, StrategicCommand } from '../strategic/types'

/** What relay sync needs from the strategic client. */
export interface RelayClient {
  command(cmd: StrategicCommand): Promise<CommandResult>
}

export interface RelaySync {
  /** Scans the Reach events since the last call. Allocation-free while no reboot ended. */
  step(events: EventRing, galaxy: Galaxy, client: RelayClient): void
  /** Skips everything already in the ring (on bind, so old events are not replayed). */
  skip(events: EventRing): void
}

function onCommandError(err: unknown): void {
  logger.warn('strategic link: setRelayOnline failed', err)
}

export function createRelaySync(): RelaySync {
  let cursor = 0
  return {
    step(events: EventRing, galaxy: Galaxy, client: RelayClient): void {
      const head = events.head
      for (let i = firstReadable(events, cursor); i < head; i++) {
        const s = eventSlot(i)
        if (events.type[s] !== REV.rebootEnd || events.other[s] !== REBOOT_END.online) continue
        const relay = galaxy.relays[events.eid[s]]
        if (relay === undefined) continue
        client.command({ type: 'setRelayOnline', relayId: relay.id, online: true }).catch(onCommandError)
      }
      cursor = head
    },
    skip(events: EventRing): void {
      cursor = events.head
    },
  }
}

/** A saved Reach: the worker's relay states into M3's `ReachState.relays` (relays mid-reboot keep theirs). */
export function applyWorkerRelays(state: ReachState, galaxy: Galaxy, relays: GalaxyEconomyView['relays']): number {
  const known = new Set(galaxy.relays.map((r) => r.id))
  let changed = 0
  for (const r of relays) {
    if (!known.has(r.id)) continue
    const rs = ensureRelayState(state, galaxy, r.id)
    if (rs.status === 'reboot') continue
    const status = r.online ? 'online' : 'offline'
    if (rs.status !== status) {
      rs.status = status
      changed++
    }
  }
  return changed
}

/** A fresh Reach: M3's online relays that the worker has offline are set online there. Resolves with the count. */
export async function pushReachRelays(state: ReachState, relays: GalaxyEconomyView['relays'], client: RelayClient): Promise<number> {
  let sent = 0
  for (const r of relays) {
    if (r.online || state.relays[r.id]?.status !== 'online') continue
    await client.command({ type: 'setRelayOnline', relayId: r.id, online: true })
    sent++
  }
  return sent
}
